import { useStream } from "@langchain/langgraph-sdk/react";
import type { Message } from "@langchain/langgraph-sdk";
import ChatConversation from "@/ui/CommunicationView/ChatConversation/ChatConversation";
import { forwardRef, useEffect, useImperativeHandle, useState, useRef } from "react";
import { LangGraphBotProps } from "./types";
import { useClaudeStream } from "../useClaudeStream";
import { LANGGRAPH_API_KEY } from "./langgraphKey";

import React from "react";

import { LANGGRAPH_DOMAIN } from "@launchos/shared-utils/env";
import ReactJson from "react-json-view";
import { MessageType } from "@/ui/CommunicationView/ChatConversation/types";
import Modal from "@/ui/Modal";
import { PopupPosition, PopupSizes } from "@/ui/Modal/types";
import { MonitorPlay } from "@phosphor-icons/react";

/**
 * Scrolls to the bottom of the chat container.
 * @param options {object} - Options object.
 *   options.onlyIfAlreadyAtBottom {boolean} - If true, only scroll if already at (or near) the bottom.
 */
const scrollToBottom = () => {
  const wn = document.getElementById("chat-conversation-container");
  if (wn && wn.scrollHeight > wn.clientHeight) {
    setTimeout(() => {
      wn.scrollTo({ top: wn.scrollHeight, behavior: "smooth" });
    }, 100);
  }
}

/**
 * Scrolls the chat container so that the newest user message appears at the top,
 * leaving maximum space below for the AI response to stream in.
 */
const scrollToShowNewMessageAtTop = () => {
  const wn = document.getElementById("chat-conversation-container");
  if (wn && wn.scrollHeight > wn.clientHeight) {
    setTimeout(() => {
      // Find the last user message element (most recently added)
      const messageElements = wn.querySelectorAll('.group.relative');
      if (messageElements.length > 0) {
        const lastMessageElement = messageElements[messageElements.length - 1] as HTMLElement;

        // Calculate the scroll position to put the message at the top
        const messageOffsetTop = lastMessageElement.offsetTop;

        // Scroll to position the message at the top of the container
        wn.scrollTo({ top: messageOffsetTop, behavior: "smooth" });
      } else {
        // Fallback to regular scroll to bottom if no messages found
        wn.scrollTo({ top: wn.scrollHeight, behavior: "smooth" });
      }
    }, 150); // Slightly longer delay to ensure the new message is rendered
  }
}

// Simple utility function for cleaning incomplete tool calls before thread.submit
export function cleanIncompleteToolCalls(messages: any[]): any[] {
  if (!messages || messages.length === 0) {
    return messages;
  }

  const cleaned: any[] = [];

  for (let i = 0; i < messages.length; i++) {
    const message = messages[i];

    // Check if this is an AI message with tool calls
    if (message.tool_calls && message.tool_calls.length > 0) {
      const toolCallIds = message.tool_calls.map((tc: any) => tc.id);
      const remainingMessages = messages.slice(i + 1);

      // Find tool response messages
      const toolResponses = remainingMessages.filter((m: any) =>
        m.type === 'tool' && toolCallIds.includes(m.tool_call_id)
      );

      // If we don't have responses for all tool calls, skip this message
      if (toolResponses.length !== toolCallIds.length) {
        continue;
      }
    }

    cleaned.push(message);
  }

  return cleaned;
}

// Usage example:
// const cleanedMessages = cleanIncompleteToolCalls(messages);
// await thread.submit(cleanedMessages, newMessage);

export const LangGraphBot = forwardRef<any, LangGraphBotProps>(
  (
    {
      engine = "langgraph",
      getScreen,
      assistantId,
      defaultThreadId,
      setDefaultThreadId = () => { },
      apiUrl = LANGGRAPH_DOMAIN,
      messagesKey = "messages",
      initialMessages = [],
      // initialThread = null,
      pinnedMessagesTop = [],
      pinnedMessagesBottom = [],
      setPinnedMessagesBottom = () => { },
      title,
      subtitle,
      sessionReplay = false,
      sessionReplaySettings = {
        speed: 200,
        countdown: 5,
      },
      suggestions,
      placeholder,
      artifactOptions = { isShowing: false },
      setArtifactOptions = () => { },
      statusBarSettings = {},
      statusBarComponent,
      withFeedbackButtons = false,
      feedbackButtons = [],
      buttonSettings = [],
      artifacts = [],
      autoScroll = true,
      onBeforeSubmit = () => { },
      onError = () => { },
      onInterrupt = () => { },
      onUpdate = () => { },
      onFinish = (thread: any, values: any) => {
        console.log("onFinish", thread, values);
      },
      onLoad = (values: any) => {
        console.log("onLoad", values);
      },
      showingRawContent = false,
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState("");
    const [didFirstMessageSubmit, setDidFirstMessageSubmit] = useState(false);
    const [threadId, setThreadId] = useState<string | null>(
      defaultThreadId || null
    );
    const [messages, setMessages] = useState<any[]>([]);
    const [countdownBannerIsShowing, setCountdownBannerIsShowing] = useState(false);
    const [isSessionReplaying, setIsSessionReplaying] = useState(false);
    const [currentCount, setCurrentCount] = useState(sessionReplaySettings.countdown);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [countdownIntervalRef, setCountdownIntervalRef] = useState<NodeJS.Timeout | null>(null);
    const [playIntervalRef, setPlayIntervalRef] = useState<NodeJS.Timeout | null>(null);
    const [playTimeoutRef, setPlayTimeoutRef] = useState<NodeJS.Timeout | null>(null);
    const [sessionReplayStarted, setSessionReplayStarted] = useState(false);
    const initialLoadProcessedRef = useRef(false);

    const apiKey = LANGGRAPH_API_KEY;

    // Enhanced error handler that filters out abort-related errors
    const handleError = (error: any) => {
      // Filter out abort-related errors that are expected when stopping threads
      if (
        error?.message?.includes?.("Aborted") ||
        error?.message?.includes?.("Controller is already closed") ||
        error?.message?.includes?.("ERR_INVALID_STATE") ||
        error?.message?.includes?.("ERR_INTERNAL_ASSERTION")
      ) {
        console.log("Thread aborted successfully:", error.message);
        return;
      }

      console.error("LangGraph error:", error);
      onError?.(error, undefined);
    };

    // Both engines are always set up (hooks can't be conditional); only one is used. The LangGraph one
    // gets no thread id while Claude is answering, so it never fetches anything.
    const claudeThread = useClaudeStream({
      threadId: engine === "claude" ? threadId : null,
      onThreadId: setThreadId,
      onFinish: (t: any) => onFinish?.(t, t?.values),
      onError: handleError,
      getScreen,
    });
    const langgraphThread = useStream<{ messages: Message[] }>({
      apiUrl,
      apiKey,
      assistantId,
      messagesKey,
      onError: handleError,
      onFinish,
      onCreated: (...args) => {
        console.log("onCreated", args);
        // onLoad?.(args);
      },
      onUpdateEvent: (...args) => {
        console.log("onUpdateEvent", args);
        if (autoScroll) scrollToBottom();
        const keys = Object.keys(args[0]);
        if (keys[0] === "__interrupt__") {
          const interruptPayload = (args[0] as any)[keys[0]][0];
          const { value = {}, resumable = false } = interruptPayload;
          onInterrupt?.(value, resumable);
        }
        onUpdate(...args);
      },
      threadId: engine === "claude" ? null : threadId || null,
      onThreadId: setThreadId,
      reconnectOnMount: engine !== "claude",
    });
    const thread = (engine === "claude" ? claudeThread : langgraphThread) as unknown as typeof langgraphThread;

    const messageResolution = [
      ...pinnedMessagesTop,
      ...(
        sessionReplay && sessionReplayStarted
          ? messages
          : thread.messages.length > 0
            ? thread.messages
            // : initialThread?.values?.messages?.length > 0
            //   ? initialThread?.values?.messages
            : messages || []
      ).map((message: Message) => {
        let from = "Bot";
        if (message.type === "human") from = "User";
        if (message.type === "tool") from = "Tool";
        if (message.type === "ai") from = "Bot";

        return {
          id: message.id,
          caption: message.content as string,
          type: "text",
          from,
          name: (message as any).name,
          artifact: (message as any)?.artifact,
          tool_call_id: (message as any).tool_call_id,
          run_id: (thread.history)?.find((itm) =>
            itm.values.messages.find((msg) => msg.id === message.id)
          )?.metadata?.run_id,
          tool_calls: (message as any)?.tool_calls,
        };
      }),
      ...pinnedMessagesBottom,
    ] as MessageType[];

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // If Shift+Enter is pressed, add a new line instead of submitting
      if (e.shiftKey && e.key === "Enter") {
        console.log("Shift+Enter pressed");
        return;
      }

      // If Enter is pressed, go ahead & submit the form
      else if (e.key === "Enter") {
        e.preventDefault();
        if (!thread.isLoading) {
          console.log("Should submit now!!");
          // Convert keyboard event to form event format expected by handleInputSubmit
          const formEvent = {
            preventDefault: () => { },
            currentTarget: document.createElement("form"),
          } as React.FormEvent<HTMLFormElement>;

          handleInputSubmit(formEvent);
        }
      }
    };

    const handleInputSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setInputValue("");

      const messages: Message[] = !didFirstMessageSubmit
        ? [...initialMessages, { type: "human", content: inputValue }]
        : [{ type: "human", content: inputValue }];

      console.log("handleInputSubmit", messages);

      const beforeSubmitState = await onBeforeSubmit?.(messages);
      console.log("beforeSubmitState", beforeSubmitState);

      if (beforeSubmitState === false) {
        e.preventDefault();
        // e.stopPropagation();
        return false; // don't submit if the beforeSubmitState is false
      }

      console.log("Submitting messages", messages);

      // alert(`Submitting ${messages.length} messages`);
      // Use custom scroll function to position new message at top
      if (autoScroll) scrollToShowNewMessageAtTop();

      // const cleanedMessages = cleanIncompleteToolCalls(messages);
      // console.log("Cleaned messages", cleanedMessages);

      await thread.submit({
        // messages: cleanedMessages,
        messages,
        ...beforeSubmitState,
      }, {
        streamResumable: true,
      });

      // @ts-ignore
      window.thread = thread; // for debugging

      if (!didFirstMessageSubmit) setDidFirstMessageSubmit(true);

      // After submission, ensure the new message is positioned at the top
      if (autoScroll) scrollToShowNewMessageAtTop();
    };

    const handleStop = () => {
      console.log("Stopping the thread");

      // Add a small delay to allow the abort to propagate
      setTimeout(() => {
        try {
          thread.stop();
        } catch (error) {
          // Ignore errors that occur during stop operation
          console.log("Stop operation completed (errors are expected):", error);
        }
      }, 100);
    };

    const handleSessionReplay = (thread: any) => {
      setSessionReplayStarted(true);
      setCountdownBannerIsShowing(true);
      setIsSessionReplaying(false);
      const { history } = thread;

      // console.log("handleSessionReplay", thread?.values?.messages?.length, initialThread?.values?.messages?.length);

      setMessages(history[1].values.messages); // start with the first message
      setArtifactOptions({ ...artifactOptions, isShowing: false });

      let count = sessionReplaySettings.countdown || 5;
      setCurrentCount(count);

      // Start the countdown
      const countdownInterval = setInterval(() => {
        count = count - 1;
        setCurrentCount(count);
        if (count <= 0) {
          clearInterval(countdownInterval);
          setCountdownIntervalRef(null);
          setCountdownBannerIsShowing(false);
          setIsSessionReplaying(true);
        }
      }, 1000);
      setCountdownIntervalRef(countdownInterval);

      // Play the messages after the countdown ends
      const startPlayInterval = () => {
        setMessages(history[1].values.messages); // start with the first message
        let idx = 1;
        const playInterval = setInterval(() => {
          if (idx < history?.length) {
            // setMessages(history.slice(0, idx + 1));
            setMessages(history[idx].values.messages);
            console.log("sessionReplay idx", idx, history[idx]);

            // Trigger onUpdate with the current history state
            onUpdate?.({ [idx]: history[idx].values });
            idx++;

            if (autoScroll) scrollToBottom();

            // Trigger this when the replay ends
            if (idx === history.length) {
              clearInterval(playInterval);
              setPlayIntervalRef(null);
              setIsSessionReplaying(false);
              onFinish?.(thread, undefined);
              if (autoScroll) setTimeout(scrollToBottom, 200);
            }
          }
        }, sessionReplaySettings.speed || 200);
        setPlayIntervalRef(playInterval);
      };

      // After the countdown ends, trigger the play interval
      const playTimeout = setTimeout(startPlayInterval,
        (sessionReplaySettings.countdown || 5) * 1000
      );
      setPlayTimeoutRef(playTimeout);
    };

    const stopReplayAndShowFullThread = () => {
      console.log("Stopping replay and showing full thread");

      // Clear any running intervals and timeouts
      if (countdownIntervalRef) {
        clearInterval(countdownIntervalRef);
        setCountdownIntervalRef(null);
      }
      if (playIntervalRef) {
        clearInterval(playIntervalRef);
        setPlayIntervalRef(null);
      }
      if (playTimeoutRef) {
        clearTimeout(playTimeoutRef);
        setPlayTimeoutRef(null);
      }

      // Stop any ongoing replay
      setIsSessionReplaying(false);
      setCountdownBannerIsShowing(false);
      setSessionReplayStarted(false);

      // Clear any existing messages and set to full thread
      setMessages(thread?.values?.messages || []);

      // Reset session replay state
      setCurrentCount((thread?.values?.messages || []).length);

      // Trigger onLoad with thread values after stopping replay
      onFinish?.(thread, undefined);
      if (autoScroll) scrollToBottom();
    };

    useImperativeHandle(ref, () => ({
      submit: (values: any) => {
        thread.submit(values);
      },
      clear: () => {
        thread.messages = [];
      },
      stopReplayAndShowFullThread,
      values: thread.values,
      currentThread: thread,
    }));

    // Only update local messages state when not streaming and not in session replay
    useEffect(() => {
      if (!sessionReplay && !thread.isLoading && thread?.values?.messages?.length > 0) {
        setMessages(thread?.values?.messages);
      }
    }, [sessionReplay, thread.isLoading, thread?.values?.messages]);

    // Handle one-time onLoad when thread.values becomes available
    useEffect(() => {
      // console.log("onLoad attempt triggered - thread.values available", thread.values, threadId, hasLoaded, sessionReplayStarted, initialLoadProcessedRef.current);

      if (threadId && thread?.values?.messages?.length > 0 && !hasLoaded && !sessionReplayStarted && !initialLoadProcessedRef.current) {
        console.log("onLoad triggered - thread.values available", thread.values);
        setHasLoaded(true);
        initialLoadProcessedRef.current = true;
        if (sessionReplay) {
          console.log("sessionReplay", thread?.values?.messages?.length);
          // handleSessionReplay(thread?.values?.messages ? thread : initialThread);
          handleSessionReplay(thread);
        } else {
          onLoad?.(thread?.values);
          if (autoScroll) scrollToBottom();
        }
      }
    }, [threadId, thread?.values?.messages?.length, hasLoaded, sessionReplay, sessionReplayStarted]);

    // Reset hasLoaded when threadId changes (new thread)
    useEffect(() => {
      if (threadId) {
        console.log("threadId changed", threadId);
        setThreadId(threadId);
        setDefaultThreadId(threadId);
        setHasLoaded(false); // Reset for new thread
        setSessionReplayStarted(false); // Reset session replay flag for new thread
        initialLoadProcessedRef.current = false; // Reset initial load ref for new thread

        // join the stream to get the latest messages
        const run_id = thread?.history?.find((itm) => itm.values.messages.find((msg) => msg.id === thread?.values?.messages[0]?.id))?.metadata?.run_id;
        if (run_id) {
          thread.joinStream(run_id as string); // join the stream to get the latest messages
          console.log("joined in progress stream", run_id);
        }

      }
    }, [threadId]);

    // Watch for changes to defaultThreadId prop and update internal threadId state
    useEffect(() => {
      if (defaultThreadId !== threadId) {
        console.log("defaultThreadId prop changed", defaultThreadId, "updating threadId");
        setThreadId(defaultThreadId || null);
        setDefaultThreadId(defaultThreadId || "");
        setHasLoaded(false); // Reset for new thread
        setSessionReplayStarted(false); // Reset session replay flag for new thread
        initialLoadProcessedRef.current = false; // Reset initial load ref for new thread
      }
    }, [defaultThreadId]);

    // Cleanup intervals and timeouts on unmount or when sessionReplay changes
    useEffect(() => {
      return () => {
        // Cleanup on unmount
        if (countdownIntervalRef) {
          clearInterval(countdownIntervalRef);
        }
        if (playIntervalRef) {
          clearInterval(playIntervalRef);
        }
        if (playTimeoutRef) {
          clearTimeout(playTimeoutRef);
        }
      };
    }, [countdownIntervalRef, playIntervalRef, playTimeoutRef]);

    // scroll to bottom when new messages are added
    useEffect(() => {
      if (autoScroll) {
        scrollToBottom();
      }
    }, [thread.messages]);

    return (
      <div
        className={`gap-6 h-full`}
      >
        {countdownBannerIsShowing && (
          <Modal
            onClose={() => setCountdownBannerIsShowing(false)}
            onSuccess={() => setCountdownBannerIsShowing(false)}
            size={PopupSizes.XXXLARGE}
            position={PopupPosition.BOTTOM}
            footer={false}
          >
            <div className="p-9 text-xl flex flex-col items-center justify-center gap-4 text-center">
              <MonitorPlay size={48} />
              <div>
                You are viewing a completed Launch OS session. <br />
                Replay will start in{" "}
                <span className="font-bold">{currentCount} seconds</span>.
              </div>
            </div>
          </Modal>
        )}

        {isSessionReplaying && sessionReplay && (
          <div className="absolute bottom-5 left-1/2 transform -translate-x-1/2 max-w-6xl w-full bg-indigo-500 z-10 p-4 rounded-2xl flex justify-center shadow-lg">
            <div className="flex items-center justify-between w-full">
              {/* Left: Icon and message */}
              <div className="flex items-center gap-3">
                <MonitorPlay size={32} className="text-white" />
                <span className="text-white text-lg font-semibold">
                  Launch OS is replaying this session...
                </span>
              </div>
              {/* Right: Buttons */}
              <div className="flex gap-3">
                <button
                  className="bg-white text-black font-medium px-4 py-2 rounded hover:bg-gray-200 transition"
                  onClick={stopReplayAndShowFullThread}
                >
                  Skip to results
                </button>
                <button
                  className="bg-transparent border border-white text-white font-medium px-4 py-2 rounded hover:bg-white hover:text-black transition"
                  onClick={() => {
                    // Try it yourself: stop replay and clear thread/messages
                    setIsSessionReplaying(false);
                    // Optionally, clear thread/messages or reset state here if needed
                    window.location.href = "/copilot";
                  }}
                >
                  Try it yourself
                </button>
              </div>
            </div>
          </div>
        )}

        {!isSessionReplaying &&
          sessionReplay &&
          !countdownBannerIsShowing && (
            <div className="absolute bottom-5 left-1/2 transform -translate-x-1/2 max-w-6xl w-full bg-green-500 z-10 p-4 rounded-2xl flex justify-center shadow-lg">
              <div className="flex items-center justify-between w-full">
                {/* Left: Icon and message */}
                <div className="flex items-center gap-3">
                  <MonitorPlay size={32} className="text-white" />
                  <span className="text-white text-lg font-semibold">
                    Launch OS session replay is complete.
                  </span>
                </div>
                {/* Right: Buttons */}
                <div className="flex gap-3">
                  <button
                    className="bg-white text-black font-medium px-4 py-2 rounded hover:bg-gray-200 transition"
                    onClick={() =>
                      handleSessionReplay(
                        // thread?.values?.messages ? thread : initialThread
                        thread
                      )
                    }
                  >
                    Watch again
                  </button>
                  <button
                    className="bg-transparent border border-white text-white font-medium px-4 py-2 rounded hover:bg-white hover:text-black transition"
                    onClick={() => {
                      setIsSessionReplaying(false);
                      window.location.href = "/copilot";
                    }}
                  >
                    Try it yourself
                  </button>
                </div>
              </div>
            </div>
          )}

        <div className="h-full w-full">
          {/* <button onClick={() => thread.stop()}>Emergency Stop</button> */}
          {!showingRawContent && (
            <ChatConversation
              containerSettings={{
                messages: messageResolution,
                withFeedbackButtons,
                feedbackButtons,
                artifacts,
                artifactOptions,
                setArtifactOptions,
                setPinnedMessagesBottom,
                isLoading: thread.isLoading,
                currentThread: thread,
              }}
              title={title}
              subtitle={subtitle}
              inputSettings={{
                type: "Rounded",
                value: inputValue,
                withButtons: true,
                buttonSettings,
                statusBarSettings,
                statusBarComponent,
                onChange: handleInputChange,
                onKeyDown: handleInputKeyDown,
                onSubmit: handleInputSubmit,
                onStop: handleStop,
                isLoading: thread.isLoading,
                suggestions,
                placeholder,
                currentThread: thread,
                hideInput: sessionReplay,
              }}
            />
          )}

          {showingRawContent && (
            <RawChatConversation
              thread={
                thread?.values?.messages
              }
              messages={(
                thread?.values?.messages || []
              ).map((message: Message) => {
                return {
                  ...message,
                  run_id: (thread?.history)?.find(
                    (itm) =>
                      itm.values.messages.find((msg) => msg.id === message.id)
                  )?.metadata?.run_id,
                };
              })}
              inputValue={inputValue}
              handleInputChange={handleInputChange}
              handleInputKeyDown={handleInputKeyDown}
              handleInputSubmit={handleInputSubmit}
            />
          )}
        </div>
      </div>
    );
  }
);

const RawChatConversation = ({
  messages,
  inputValue,
  handleInputChange,
  handleInputKeyDown,
  handleInputSubmit,
  thread,
}: {
  messages: Message[];
  inputValue: string;
  thread: any;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleInputSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
}) => {
  return (
    <div className="flex flex-col gap-2 bg-white/90 rounded-md p-2">
      <div
        style={{ height: "85vh" }}
        className="border border-gray-300 rounded-md p-2 overflow-y-auto"
      >
        <ReactJson src={thread} collapsed={1} name="thread" />
        {/* {messages.map((message) => (
          <div
            key={message.id}
            className="bg-gray-100 text-black p-2 rounded-md"
          >
            <ReactJson
              name={message.type}
              src={message}
              collapsed
              indentWidth={2}
              // theme="monokai"
              style={{
                fontSize: 16,
              }}
              displayDataTypes={false}
              displayObjectSize={false}
              sortKeys
              quotesOnKeys={false}
            />
          </div>
        ))} */}
      </div>
      <div className="flex flex-row gap-2 border border-gray-300 rounded-md p-2">
        <input
          className="w-full"
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
        />
        {/* <button onClick={() => handleInputSubmit({ preventDefault: () => {}, currentTarget: document.createElement("form") } as React.FormEvent<HTMLFormElement>)}>Submit</button> */}
      </div>
    </div>
  );
};
